import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  Tooltip,
  HStack,
  VStack,
  Text,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Progress,
  useDisclosure,
  Icon,
  IconButton,
  Collapse,
  Grid,
  useToken,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { Header, ColumnInfo, ModelInfo, ObservationColumnProto } from "build-ts/metadata";
import {
  AggregationType,
  ColumnType,
  DataType,
  dataTypeToJSON,
} from "build-ts/enums";
import OntologyEntitySelector from "./OntologyEntitySelector";
import CreateKGColumnComponent from "./CreateKGColumnComponent";
import EditMetricDefinitionContainer from "../../containers/configuration/EditMetricDefinitionContainer";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { useNavigate } from "react-router-dom";
import { TbMathFunction } from "react-icons/tb";
import { FiSave, FiCalendar } from "react-icons/fi";
import { BiSolidNetworkChart } from "react-icons/bi";
import { MdBarChart, MdNumbers, MdFilterList } from "react-icons/md";
import { FaChartPie } from "react-icons/fa";
import { iconForKgColumn, kgCreateModalTitle } from "../../utils/ontologyEntityIcons";

interface OntologyMappingProps {
  loading: boolean;
  modelInfo?: ModelInfo | undefined;
  addedColumns?: ColumnInfo[] | undefined;
  removedColumns?: ColumnInfo[] | undefined;
  header: Header;
  columns: ColumnInfo[];
  kgColumns: Header[];
  dependentKgColumns: Map<string, Header[]>;
  dependentMetrics: Map<string, Header[]>;
  handleHeaderChange: (property: string, value: string) => void;
  handleColumnChange: (index: number, property: "data_type" | "column_type" | "default_aggregation", value: string) => void;
  onKgSelectionChange: (columnIndex: number, kgColumn: Header | null) => void;
  onMetricCreated: (columnIndex: number, header: Header) => void;
  handleSubmit: () => void;
  updateFlag: boolean;
  buttonLoader: boolean;
  refreshKGColumns: () => Promise<Header[]>;
  handleQueueMetricRequest: (request: CreateOrUpdateMetrics_Request) => void;
  metricRequestList: CreateOrUpdateMetrics_Request[];
  onBack: () => void;
}

function columnIndexById(columns: ColumnInfo[], id: string | undefined): number {
  if (!id) return -1;
  return columns.findIndex((c) => c.header?.id === id);
}

/** Mapped panel row: status · source (name + type) · arrow · ontology selector — first track must fit 10px dot + glow (was 12px and clipped box-shadow). */
const MAPPED_ROW_GRID = "32px minmax(130px,0.95fr) 22px minmax(200px,1.35fr)";

/** Max width for the mapped-panel search field (center column). */
const MAPPED_PANEL_SEARCH_MAX_W = "260px";

/** Thin, neutral scrollbar for unmapped / mapped column lists (Firefox + WebKit). */
const MAP_PANEL_SCROLL_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#CBD5E0 transparent",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#CBD5E0",
    borderRadius: "6px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#A0AEC0",
  },
};

type UnmappedSortOption = "default" | "name-asc" | "name-desc";

type MappedSortOption = "mapping" | "name-asc" | "name-desc" | "created" | "modified";

function matchesSearch(query: string, name: string | undefined): boolean {
  const q = query.trim().toLowerCase();
  return !q || (name || "").toLowerCase().includes(q);
}

const MAPPED_SORT_LABEL: Record<MappedSortOption, string> = {
  mapping: "Sort",
  "name-asc": "A→Z",
  "name-desc": "Z→A",
  created: "Created",
  modified: "Modified",
};

const UNMAPPED_SORT_LABEL: Record<UnmappedSortOption, string> = {
  default: "Sort",
  "name-asc": "A→Z",
  "name-desc": "Z→A",
};

function headerSortMs(h: Header | undefined, kind: "created" | "modified"): number {
  if (!h) return 0;
  if (kind === "modified") {
    const m = h.last_modified_date_ms ?? h.created_date_ms;
    return m !== null ? Number(m) : 0;
  }
  const c = h.created_date_ms;
  return c !== null ? Number(c) : 0;
}

const MAP_HEADER_ENTITY_TOOLTIP_MAX = 25;

function MapHeaderHoverEntityList({ title, items }: { title: string; items: string[] }) {
  const shown = items.slice(0, MAP_HEADER_ENTITY_TOOLTIP_MAX);
  const rest = items.length - shown.length;
  return (
    <Box maxW="280px" maxH="220px" overflowY="auto" py={0.5}>
      <Text fontWeight="700" fontSize="xs" mb={1} color="white">
        {title}
      </Text>
      {shown.map((line, i) => (
        <Text key={i} fontSize="xs" color="whiteAlpha.900" mb={0} lineHeight="1.35">
          • {line}
        </Text>
      ))}
      {rest > 0 ? (
        <Text fontSize="xs" color="whiteAlpha.700" mt={1} mb={0}>
          …and {rest} more
        </Text>
      ) : null}
    </Box>
  );
}

/** Icon + count + type label; tooltip lists entities (same pattern as ViewMetricByModel cards). */
function MapHeaderStatChip({
  icon,
  value,
  name,
  tooltipTitle,
  names,
  ariaLabel,
}: {
  icon: React.ElementType;
  value: number;
  name: string;
  tooltipTitle: string;
  names: string[];
  ariaLabel: string;
}) {
  const typeLabel = value === 1 ? name : name + "s";
  return (
    <Tooltip
      label={<MapHeaderHoverEntityList title={tooltipTitle} items={names} />}
      hasArrow
      openDelay={250}
      placement="top"
      bg="gray.700"
    >
      <HStack
        spacing={1.5}
        as="span"
        align="center"
        py={1}
        px={2}
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.400"

        flexShrink={0}
        cursor="default"
        aria-label={`${ariaLabel}: ${value}`}
      >
        <Icon as={icon} boxSize={4} color="purple.700" aria-hidden flexShrink={0} />
        <Text fontSize="sm" fontWeight="700" color="purple.700" mb={0} lineHeight="1">
          {value}
        </Text>
        <Text fontSize="sm" fontWeight="600" color="purple.700" mb={0} lineHeight="1">
          {typeLabel}
        </Text>
      </HStack>
    </Tooltip>
  );
}

const OntologyMapping: React.FC<OntologyMappingProps> = ({
  loading,
  modelInfo,
  addedColumns,
  removedColumns,
  header,
  columns,
  kgColumns,
  dependentKgColumns,
  dependentMetrics,
  handleHeaderChange,
  handleColumnChange,
  onKgSelectionChange,
  onMetricCreated,
  handleSubmit,
  updateFlag,
  buttonLoader,
  refreshKGColumns,
  handleQueueMetricRequest,
  metricRequestList,
  onBack,
}) => {
  const navigate = useNavigate();
  const [unmappedSearchQuery, setUnmappedSearchQuery] = useState("");
  const [unmappedSort, setUnmappedSort] = useState<UnmappedSortOption>("default");
  const [mappedSearchQuery, setMappedSearchQuery] = useState("");
  const [mappedSortOption, setMappedSortOption] = useState<MappedSortOption>("mapping");
  const [pendingPromoteIds, setPendingPromoteIds] = useState<Set<string>>(() => new Set());
  const [rightPanelIds, setRightPanelIds] = useState<string[]>([]);
  const [dismissedRemovedIds, setDismissedRemovedIds] = useState<Set<string>>(() => new Set());
  const [removedSectionOpen, setRemovedSectionOpen] = useState(false);
  const [autoOpenColumnId, setAutoOpenColumnId] = useState<string | null>(null);

  const { isOpen: isKGModalOpen, onOpen: onKGModalOpen, onClose: onKGModalClose } = useDisclosure();
  const [kgModalColumnIndex, setKgModalColumnIndex] = useState<number | null>(null);
  const [kgModalColumnType, setKgModalColumnType] = useState<ColumnType>(ColumnType.ATTRIBUTE);
  const [kgModalDefaultName, setKgModalDefaultName] = useState<string>("");

  const handleOpenCreateKGColumnModal = (columnType: ColumnType, columnIndex: number, defaultName: string) => {
    setKgModalColumnIndex(columnIndex);
    setKgModalColumnType(columnType);
    setKgModalDefaultName(defaultName);
    onKGModalOpen();
  };

  const handleKGColumnCreated = (createdHeader: Header) => {
    if (kgModalColumnIndex !== null) {
      onKgSelectionChange(kgModalColumnIndex, createdHeader);
    }
    onKGModalClose();
  };

  const { isOpen: isMetricModalOpen, onOpen: onMetricModalOpen, onClose: onMetricModalClose } = useDisclosure();
  const [metricModalColumnIndex, setMetricModalColumnIndex] = useState<number | null>(null);
  const [metricModalLogicalColumn, setMetricModalLogicalColumn] = useState<ColumnInfo | null>(null);

  const handleOpenCreateMetricModal = (columnIndex: number, logicalColumn: ColumnInfo) => {
    setMetricModalColumnIndex(columnIndex);
    setMetricModalLogicalColumn(logicalColumn);
    onMetricModalOpen();
  };

  // Case 1 (model exists, updateFlag=true): metric was created via API
  const handleMetricCreated = (createdHeader: Header) => {
    if (metricModalColumnIndex !== null) {
      onMetricCreated(metricModalColumnIndex, createdHeader);
    }
    onMetricModalClose();
  };

  // Case 2 (model not yet saved, updateFlag=false): queue the full request
  const handleMetricDeferred = (formData: CreateOrUpdateMetrics_Request) => {
    handleQueueMetricRequest(formData);
    onMetricModalClose();
  };

  const addedColumnIds = useMemo(
    () => new Set((addedColumns ?? []).map(c => c.header?.id).filter(Boolean)),
    [addedColumns]
  );

  // Combined metrics: existing from backend + newly queued (pre-save)
  const getCombinedMetrics = useCallback((columnId: string): Header[] => {
    const existingMetrics = dependentMetrics.get(columnId) || [];
    const newMetrics: Header[] = [];
    metricRequestList.forEach(request => {
      request.metrics?.forEach((metric) => {
        const observationColumns = metric.definition?.observation_columns || [];
        const isForThisColumn = observationColumns.some(
          (obsCol) => obsCol.logical_column_header?.id === columnId
        );
        if (isForThisColumn && metric.header) {
          newMetrics.push(metric.header);
        }
      });
    });
    return [...existingMetrics, ...newMetrics];
  }, [dependentMetrics, metricRequestList]);

  const isLogicalMapped = useCallback(
    (col: ColumnInfo) => Boolean(dependentKgColumns.get(col.header?.id || "")?.[0]),
    [dependentKgColumns]
  );

  const updatedColumnIds = useMemo(() => {
    const set = new Set<string>();
    if (!modelInfo?.columns?.length) return set;
    const byId = new Map(modelInfo.columns.map((c) => [c.header?.id, c] as const));
    for (const c of columns) {
      const id = c.header?.id;
      if (!id) continue;
      const orig = byId.get(id);
      if (orig && orig.data_type !== c.data_type) set.add(id);
    }
    return set;
  }, [columns, modelInfo]);

  const totalNonRemoved = columns.length;
  const mappedCount = useMemo(
    () => columns.filter((c) => isLogicalMapped(c)).length,
    [columns, isLogicalMapped]
  );
  const progressPct =
    totalNonRemoved > 0 ? Math.min(100, Math.round((mappedCount / totalNonRemoved) * 100)) : 0;

  const leftPanelCount = totalNonRemoved - mappedCount;

  const rightPanelRowCount = useMemo(() => {
    let n = 0;
    for (const id of rightPanelIds) {
      const idx = columnIndexById(columns, id);
      if (idx < 0) continue;
      const mapped = isLogicalMapped(columns[idx]);
      if (mapped || pendingPromoteIds.has(id)) n += 1;
    }
    return n;
  }, [rightPanelIds, columns, isLogicalMapped, pendingPromoteIds]);

  const visibleRemovedColumns = useMemo(
    () =>
      (removedColumns ?? []).filter(
        (c) => c.header?.id && !dismissedRemovedIds.has(c.header.id)
      ),
    [removedColumns, dismissedRemovedIds]
  );

  const mappedVisibleIds = useMemo(() => {
    const filtered = rightPanelIds.filter((id) => {
      const idx = columnIndexById(columns, id);
      if (idx < 0) return false;
      const col = columns[idx];
      if (!matchesSearch(mappedSearchQuery, col.header?.name)) return false;
      return isLogicalMapped(col) || pendingPromoteIds.has(id);
    });

    if (mappedSortOption === "mapping") return filtered;

    const enriched = filtered
      .map((id) => {
        const idx = columnIndexById(columns, id);
        if (idx < 0) return null;
        return { id, col: columns[idx] };
      })
      .filter((x): x is { id: string; col: ColumnInfo } => x !== null);

    const getName = (c: ColumnInfo) => (c.header?.name || "").toLowerCase();
    const arr = [...enriched];
    switch (mappedSortOption) {
      case "name-asc":
        arr.sort((a, b) => getName(a.col).localeCompare(getName(b.col)));
        break;
      case "name-desc":
        arr.sort((a, b) => getName(b.col).localeCompare(getName(a.col)));
        break;
      case "created":
        arr.sort(
          (a, b) =>
            headerSortMs(b.col.header, "created") - headerSortMs(a.col.header, "created")
        );
        break;
      case "modified":
        arr.sort(
          (a, b) =>
            headerSortMs(b.col.header, "modified") - headerSortMs(a.col.header, "modified")
        );
        break;
      default:
        break;
    }
    return arr.map((x) => x.id);
  }, [
    rightPanelIds,
    columns,
    pendingPromoteIds,
    isLogicalMapped,
    mappedSearchQuery,
    mappedSortOption,
  ]);

  const unmappedDisplayEntries = useMemo(() => {
    const entries: { column: ColumnInfo; index: number }[] = [];
    columns.forEach((c, i) => {
      if (isLogicalMapped(c)) return;
      if (!matchesSearch(unmappedSearchQuery, c.header?.name)) return;
      entries.push({ column: c, index: i });
    });
    if (unmappedSort === "name-asc") {
      entries.sort((a, b) =>
        (a.column.header?.name || "").localeCompare(b.column.header?.name || "", undefined, {
          sensitivity: "base",
        })
      );
    } else if (unmappedSort === "name-desc") {
      entries.sort((a, b) =>
        (b.column.header?.name || "").localeCompare(a.column.header?.name || "", undefined, {
          sensitivity: "base",
        })
      );
    }
    return entries;
  }, [columns, isLogicalMapped, unmappedSearchQuery, unmappedSort]);

  const mapHeaderEntityStats = useMemo(() => {
    const metricHeaders: Header[] = [];
    const seenMetric = new Set<string>();
    for (const list of Array.from(dependentMetrics.values())) {
      for (const h of list ?? []) {
        const mid = h.id;
        if (mid && !seenMetric.has(mid)) {
          seenMetric.add(mid);
          metricHeaders.push(h);
        }
      }
    }

    const attributeLinkLabels: string[] = [];
    const measureLinkLabels: string[] = [];
    const temporalLinkLabels: string[] = [];

    const colNameById = new Map<string, string>();
    for (const c of columns) {
      const cid = c.header?.id;
      if (cid) colNameById.set(cid, c.header?.name?.trim() || "—");
    }

    for (const [lcId, kgList] of Array.from(dependentKgColumns.entries())) {
      const lcName = colNameById.get(lcId) ?? "—";
      for (const kg of kgList ?? []) {
        const kgName = kg.name?.trim() || "Unknown KG column";
        const line = `${kgName} · ${lcName}`;
        const ct = kg.column_type;
        if (ct === ColumnType.ATTRIBUTE) attributeLinkLabels.push(line);
        else if (ct === ColumnType.MEASURE) measureLinkLabels.push(line);
        else if (ct === ColumnType.TEMPORAL) temporalLinkLabels.push(line);
      }
    }

    return {
      metricCount: metricHeaders.length,
      metricNames: metricHeaders.map((h) => h.name?.trim() || h.id || "Unnamed metric"),
      attributeLinkLabels,
      measureLinkLabels,
      temporalLinkLabels,
    };
  }, [header?.id, dependentMetrics, dependentKgColumns, columns]);

  useEffect(() => {
    if (loading) return;
    setRightPanelIds((prev) => {
      // Seed right panel with KG-mapped columns on load/reload
      const candidateEntries = columns
        .map((c, i) => ({ id: c.header?.id, i }))
        .filter((x): x is { id: string; i: number } =>
          Boolean(x.id && isLogicalMapped(columns[x.i]))
        )
        .sort((a, b) => b.i - a.i);
      const seen = new Set(prev);
      let next = [...prev];
      for (const { id } of candidateEntries) {
        if (!seen.has(id)) {
          next.unshift(id);
          seen.add(id);
        }
      }
      next = next.filter((id) => {
        const idx = columnIndexById(columns, id);
        if (idx < 0) return false;
        return isLogicalMapped(columns[idx]) || pendingPromoteIds.has(id);
      });
      return next;
    });
  }, [loading, columns, isLogicalMapped, pendingPromoteIds]);

  const handlePromoteFromLeft = useCallback((column: ColumnInfo, index: number) => {
    const id = column.header?.id;
    if (!id) return;
    setPendingPromoteIds((p) => new Set(p).add(id));
    setRightPanelIds((ids) => [id, ...ids.filter((x) => x !== id)]);
    setAutoOpenColumnId(id);
  }, []);

  const handleRightSelectorMenuClose = useCallback((columnId: string, hasKgSelection: boolean) => {
    setAutoOpenColumnId((prev) => (prev === columnId ? null : prev));
    setPendingPromoteIds((p) => {
      const n = new Set(p);
      n.delete(columnId);
      return n;
    });
    if (!hasKgSelection) {
      setRightPanelIds((ids) => ids.filter((x) => x !== columnId));
    }
  }, []);

  const tsWorksheetId = modelInfo?.data_source?.ts_worksheet_id;

  const [dotGreen500, dotGreen200, dotOrange600, dotOrange300, dotGray400, dotGray200] = useToken(
    "colors",
    ["green.500", "green.200", "orange.600", "orange.300", "gray.400", "gray.200"]
  );

  const dataTypeSelect = (col: ColumnInfo, index: number, readOnly: boolean) => (
    <Select
      size="xs"
      w="fit-content"
      maxW="140px"
      value={dataTypeToJSON(col.data_type!) || ""}
      onChange={(e) => handleColumnChange(index, "data_type", e.target.value)}
      isDisabled={readOnly}
      bg={readOnly ? "transparent" : "white"}
      borderRadius="md"
      fontWeight="600"
      fontSize="11px"
      borderColor="gray.300"
    >
      <option value={dataTypeToJSON(DataType.VARCHAR)}>VARCHAR</option>
      <option value={dataTypeToJSON(DataType.BOOLEAN)}>BOOLEAN</option>
      <option value={dataTypeToJSON(DataType.INT32)}>INT32</option>
      <option value={dataTypeToJSON(DataType.INT64)}>INT64</option>
      <option value={dataTypeToJSON(DataType.DOUBLE)}>DOUBLE</option>
      <option value={dataTypeToJSON(DataType.DATE)}>DATE</option>
      <option value={dataTypeToJSON(DataType.DATETIME)}>DATETIME</option>
    </Select>
  );

  const mappedRowStatusDot = (opts: { mapped: boolean; isNew: boolean; pending: boolean }) => {
    const variant: "blue" | "green" | "gray" =
      opts.pending || (opts.isNew && opts.mapped) ? "blue" : opts.mapped ? "green" : "gray";

    let fill: string;
    let boxShadow: string;
    if (variant === "blue") {
      fill = dotOrange600;
      boxShadow = `0 0 6px ${dotOrange300}, 0 0 12px ${dotOrange300}`;
    } else if (variant === "green") {
      fill = dotGreen500;
      boxShadow = `0 0 6px ${dotGreen200}, 0 0 12px ${dotGreen200}`;
    } else {
      fill = dotGray400;
      boxShadow = `0 0 5px ${dotGray200}, 0 0 10px ${dotGray200}`;
    }

    return (
      <Box
        as="span"
        display="inline-block"
        w="10px"
        h="10px"
        borderRadius="full"
        bg={fill}
        flexShrink={0}
        aria-hidden
        boxShadow={boxShadow}
      />
    );
  };

  return (
    <Box
      flex={1}
      minH={0}
      h="100%"
      display="flex"
      flexDirection="column"
      bg="white"
      overflow="hidden"
    >
      {loading ? (
        <Flex flex={1} minH={0} justify="center" align="center" gap={3}>
          <Spinner size="md" color="purple.500" thickness="2px" />
          <Text color="gray.600" fontSize="md" m={0}>
            Loading model information...
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" flex={1} minH={0} overflow="hidden">
          {/* Fixed toolbar (does not scroll with rows) */}
          <Box
            flexShrink={0}
            zIndex={20}
            bg="white"
            borderBottomWidth="1px"
            borderColor="gray.200"
            px={{ base: 3, md: 6 }}
            py={3}
            mb={4}
            boxShadow="sm"
          >
            <Flex align="center" justify="space-between" gap={4} flexWrap="wrap" mb={3}>
              <Breadcrumb separator={<ChevronRightIcon color="gray.400" boxSize={3} />} fontSize="sm" flex="1" minW={0}>
                <BreadcrumbItem>
                  <BreadcrumbLink onClick={() => navigate("/mkg/models")} color="purple.600">
                    Data Models
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <Text as="span" color="gray.700" fontWeight="600" mb={0}>
                    {header?.name || "Model"}
                  </Text>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <Text as="span" color="gray.500" mb={0}>
                    Map
                  </Text>
                </BreadcrumbItem>
              </Breadcrumb>
              {/* Top-right buttons: Back, Edit Model, Save Mapping */}
              <HStack spacing={2} flexShrink={0}>
                <Button variant="outline" colorScheme="purple" onClick={onBack} leftIcon={<Text mb={0}>←</Text>} size="md">
                  Back
                </Button>
                <Tooltip label="Add Formula Column" hasArrow bg="gray.700" color="white" fontSize="xs">
                  <Button
                    aria-label="Add Formula Column"
                    leftIcon={<Icon as={TbMathFunction} boxSize={5} />}
                    variant="solid"
                    size="md"
                    colorScheme="purple"
                    isDisabled={!tsWorksheetId}
                    onClick={() => tsWorksheetId && navigate(`/mkg/thoughtspot/${tsWorksheetId}`)}
                  >
                    <Text mb={0} fontWeight="700">Edit Model</Text>
                  </Button>
                </Tooltip>

                <Button colorScheme="green" bg="green.600" color="white"
                  _hover={{ bg: "green.700" }}
                  onClick={handleSubmit} isLoading={buttonLoader} size="md"
                  leftIcon={<Icon as={FiSave} boxSize={5} />}>
                  <Text mb={0} fontWeight="700">Save Mapping</Text>
                </Button>
              </HStack>
            </Flex>
            {/* Name and Description of the model */}
            <VStack align="stretch" spacing={0} pt={1} borderTopWidth="1px" borderColor="gray.100">
              <Flex align="center" gap={3} flexWrap="wrap" py={2} w="100%">
                <HStack
                  align="center"
                  spacing={2}
                  flex={{ base: "1 1 100%", md: "1 1 0" }}
                  minW={0}
                >
                  <Text fontSize="xs" fontWeight="700" color="gray.500" mb={0} whiteSpace="nowrap">
                    Name
                  </Text>
                  <Input
                    flex={1}
                    minW={0}
                    placeholder="Model name"
                    value={header?.name || ""}
                    onChange={(e) => handleHeaderChange("name", e.target.value)}
                    fontWeight="600"
                    size="sm"
                  />
                </HStack>
                <HStack
                  align="center"
                  spacing={2}
                  flex={{ base: "1 1 100%", md: "1 1 0" }}
                  minW={0}
                >
                  <Text fontSize="xs" fontWeight="700" color="gray.500" mb={0} whiteSpace="nowrap">
                    Description
                  </Text>
                  <Input
                    flex={1}
                    minW={0}
                    placeholder="Model description"
                    value={header?.description || ""}
                    onChange={(e) => handleHeaderChange("description", e.target.value)}
                    fontSize="sm"
                    size="sm"
                  />
                </HStack>
              </Flex>

              {/* Progress Bar */}
              <Flex
                w="100%"
                align="center"
                justify="space-between"
                gap={3}
                flexWrap="wrap"
                py={2}
                borderTopWidth="1px"
                borderColor="gray.100"
              >
                <HStack spacing={3} align="center" flexShrink={0} minW={0}
                  borderWidth="1px"
                  borderColor="gray.300"
                  borderRadius="full"
                  p={1}>
                  <Box
                    w={{ base: "min(100%, 240px)", sm: "240px" }}
                    maxW="240px"
                    minW={{ base: "200px", sm: "200px" }}
                    flexShrink={0}
                    p={1}
                  >
                    <Progress
                      w="100%"
                      value={progressPct}
                      size="sm"
                      colorScheme="green"
                      borderRadius="full"
                      bg="gray.200"
                    />
                  </Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.700" mb={0} mr={1}>
                    {mappedCount} / {totalNonRemoved} {mappedCount > 1 ? "columns" : "column"} in ontology
                  </Text>
                </HStack>
                <HStack
                  spacing={2}
                  flexWrap="wrap"
                  justify="flex-end"
                  flex={{ base: "1 1 100%", md: "0 0 auto" }}
                  minW={0}
                >
                  {mapHeaderEntityStats.metricCount > 0 ? (
                    <MapHeaderStatChip
                      icon={MdBarChart}
                      value={mapHeaderEntityStats.metricCount}
                      name="Metric"
                      tooltipTitle="Metrics"
                      names={mapHeaderEntityStats.metricNames}
                      ariaLabel="Metrics on this model"
                    />
                  ) : null}
                  {mapHeaderEntityStats.attributeLinkLabels.length > 0 ? (
                    <MapHeaderStatChip
                      icon={FaChartPie}
                      value={mapHeaderEntityStats.attributeLinkLabels.length}
                      name="Attribute"
                      tooltipTitle="Attributes"
                      names={mapHeaderEntityStats.attributeLinkLabels}
                      ariaLabel="Attribute links in knowledge graph"
                    />
                  ) : null}
                  {mapHeaderEntityStats.measureLinkLabels.length > 0 ? (
                    <MapHeaderStatChip
                      icon={MdNumbers}
                      value={mapHeaderEntityStats.measureLinkLabels.length}
                      name="Measure"
                      tooltipTitle="Measures"
                      names={mapHeaderEntityStats.measureLinkLabels}
                      ariaLabel="Measure links in knowledge graph"
                    />
                  ) : null}
                  {mapHeaderEntityStats.temporalLinkLabels.length > 0 ? (
                    <MapHeaderStatChip
                      icon={FiCalendar}
                      value={mapHeaderEntityStats.temporalLinkLabels.length}
                      name="Timeframe"
                      tooltipTitle="Timeframes"
                      names={mapHeaderEntityStats.temporalLinkLabels}
                      ariaLabel="Datetime links in knowledge graph"
                    />
                  ) : null}
                </HStack>
              </Flex>
            </VStack>
          </Box>

          {/* Two-panel split: unmapped | mapped */}
          <Flex
            flex={1}
            minH={0}
            overflow="hidden"
            px={{ base: 3, md: 6 }}
            pb={4}
            gap={0}
            direction={{ base: "column", md: "row" }}
            align="stretch"
          >
            {/* Unmapped columns */}
            <Flex
              flex={{ base: "1", md: "0 0 30%" }}
              w={{ base: "100%", md: "30%" }}
              maxW={{ md: "360px" }}
              minW={{ md: 0 }}
              direction="column"
              borderRightWidth={{ md: "1px" }}
              borderBottomWidth={{ base: "1px", md: 0 }}
              borderColor="gray.400"
              pr={{ md: 3 }}
              pb={{ base: 3, md: 0 }}
            >
              <VStack align="stretch" spacing={2} mb={2} flexShrink={0} w="100%">
                <Flex align="center" justify="space-between" gap={2} flexWrap="wrap" w="100%">
                  <Text fontSize="sm" fontWeight="800" color="gray.700" mb={0} flexShrink={0}>
                    Unmapped ({leftPanelCount})
                  </Text>
                  <Menu placement="bottom-end" closeOnSelect>
                    <MenuButton
                      as={Button}
                      size="xs"
                      variant={unmappedSort === "default" ? "outline" : "solid"}
                      colorScheme={unmappedSort === "default" ? "gray" : "purple"}
                      flexShrink={0}
                      fontWeight="600"
                      leftIcon={<Icon as={MdFilterList} boxSize={3.5} aria-hidden />}
                      rightIcon={<ChevronDownIcon boxSize={3} />}
                    >
                      {UNMAPPED_SORT_LABEL[unmappedSort]}
                    </MenuButton>
                    <MenuList fontSize="sm" minW="200px" zIndex={30}>
                      <MenuItem
                        fontWeight={unmappedSort === "default" ? "700" : "400"}
                        icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                        onClick={() => setUnmappedSort("default")}
                      >
                        Default order
                      </MenuItem>
                      <MenuItem
                        fontWeight={unmappedSort === "name-asc" ? "700" : "400"}
                        icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                        onClick={() =>
                          setUnmappedSort((s) => (s === "name-asc" ? "default" : "name-asc"))
                        }
                      >
                        Name A→Z
                      </MenuItem>
                      <MenuItem
                        fontWeight={unmappedSort === "name-desc" ? "700" : "400"}
                        icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                        onClick={() =>
                          setUnmappedSort((s) => (s === "name-desc" ? "default" : "name-desc"))
                        }
                      >
                        Name Z→A
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
                <Input
                  size="sm"
                  w="100%"
                  placeholder="Search columns..."
                  value={unmappedSearchQuery}
                  onChange={(e) => setUnmappedSearchQuery(e.target.value)}
                  bg="gray.50"
                  borderRadius="md"
                />
              </VStack>
              <Box
                flex={1}
                minH={0}
                overflowY="auto"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                sx={MAP_PANEL_SCROLL_SX}
              >
                {leftPanelCount === 0 ? (
                  <Box py={8} px={3} textAlign="center">
                    <Text color="gray.500" fontSize="sm" mb={0}>
                      No unmapped columns.
                    </Text>
                  </Box>
                ) : unmappedDisplayEntries.length === 0 ? (
                  <Box py={8} px={3} textAlign="center">
                    <Text color="gray.500" fontSize="sm" mb={0}>
                      No columns match your search.
                    </Text>
                  </Box>
                ) : (
                  unmappedDisplayEntries.map(({ column, index }) => {
                    const id = column.header?.id;
                    const isNew = Boolean(id && addedColumnIds.has(id));
                    const isUpdated = Boolean(id && updatedColumnIds.has(id));
                    const awaitingOntologyOnRight = Boolean(id && pendingPromoteIds.has(id));
                    const columnMetrics = id ? getCombinedMetrics(id) : [];
                    const hasMetrics = columnMetrics.length > 0;
                    return (
                      <Box
                        key={`left-${id ?? index}`}
                        position="relative"
                        w="100%"
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handlePromoteFromLeft(column, index);
                          }
                        }}
                        _focusVisible={{
                          outline: "2px solid",
                          outlineColor: "purple.500",
                          outlineOffset: 2,
                        }}
                      >
                        {/* Full-row click target; interactive children use pointer-events: auto */}
                        <Box
                          position="absolute"
                          inset={0}
                          zIndex={0}
                          cursor="pointer"
                          aria-hidden
                          _hover={{ bg: awaitingOntologyOnRight ? "orange.100" : "gray.50" }}
                          bg={awaitingOntologyOnRight ? "orange.100" : "transparent"}
                          onClick={() => handlePromoteFromLeft(column, index)}
                        />
                        <HStack
                          position="relative"
                          zIndex={1}
                          justify="space-between"
                          align="flex-start"
                          spacing={2}
                          px={3}
                          py={3}
                          pointerEvents="none"
                        >
                          <VStack align="start" flex={1} minW={0} spacing={2}>
                            <HStack spacing={2} align="center" flexWrap="wrap">
                              <Text fontWeight="700" color="gray.900" mb={0} fontSize="sm" textAlign="left">
                                {column.header?.name || ""}
                              </Text>
                              {isNew && (
                                <Badge colorScheme="blue" fontSize="9px" variant="subtle">
                                  new
                                </Badge>
                              )}
                              {isUpdated && (
                                <Badge colorScheme="orange" fontSize="9px" variant="subtle">
                                  updated
                                </Badge>
                              )}
                              {hasMetrics && (
                                <Tooltip
                                  label={columnMetrics.map((m) => m.name).join(", ")}
                                  hasArrow
                                  bg="gray.700"
                                  color="white"
                                  fontSize="xs"
                                  openDelay={200}
                                  placement="top"
                                >
                                  <HStack
                                    spacing={0.5}
                                    as="span"
                                    pointerEvents="auto"
                                    px={1.5}
                                    py={0.5}
                                    borderRadius="md"
                                    bg="purple.50"
                                    borderWidth="1px"
                                    borderColor="purple.200"
                                  >
                                    <Icon as={MdBarChart} boxSize={3} color="purple.600" />
                                    <Text mb={0} fontSize="9px" fontWeight="700" color="purple.600" lineHeight="1">
                                      {columnMetrics.length}
                                    </Text>
                                  </HStack>
                                </Tooltip>
                              )}
                            </HStack>
                            <Box
                              pointerEvents="auto"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              {dataTypeSelect(column, index, false)}
                            </Box>
                          </VStack>
                          <Tooltip
                            label={
                              awaitingOntologyOnRight
                                ? "Finish ontology mapping in the Mapped panel →"
                                : "Click to map this column"
                            }
                            hasArrow
                            openDelay={400}
                            placement="left"
                          >
                            <Box
                              flexShrink={0}
                              pt={0.5}
                              pointerEvents="auto"
                              cursor="pointer"
                              aria-hidden
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePromoteFromLeft(column, index);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              {awaitingOntologyOnRight ? (
                                <Icon as={BiSolidNetworkChart} color="orange.700" boxSize={7} />
                              ) : (
                                <Icon as={ChevronRightIcon} color="gray.400" boxSize={5} />
                              )}
                            </Box>
                          </Tooltip>
                        </HStack>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Flex>

            {/* Mapped columns */}
            <Flex flex="1" minW={0} direction="column" pl={{ md: 3 }} pt={{ base: 2, md: 0 }}>
              <Flex align="center" gap={2} mb={2} flexShrink={0} w="100%" minW={0}>
                <Text fontSize="sm" fontWeight="800" color="gray.700" mb={0} flexShrink={0}>
                  Mapped ({rightPanelRowCount})
                </Text>
                <Flex flex={1} justify="center" minW={0} px={1}>
                  <Input
                    w="100%"
                    maxW={MAPPED_PANEL_SEARCH_MAX_W}
                    size="sm"
                    placeholder="Search..."
                    value={mappedSearchQuery}
                    onChange={(e) => setMappedSearchQuery(e.target.value)}
                    bg="gray.50"
                    borderRadius="md"
                  />
                </Flex>
                <Menu placement="bottom-end" closeOnSelect>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant={mappedSortOption === "mapping" ? "outline" : "solid"}
                    colorScheme={mappedSortOption === "mapping" ? "gray" : "purple"}
                    flexShrink={0}
                    fontWeight="600"
                    leftIcon={<Icon as={MdFilterList} boxSize={4} aria-hidden />}
                    rightIcon={<ChevronDownIcon boxSize={3} />}
                  >
                    {MAPPED_SORT_LABEL[mappedSortOption]}
                  </MenuButton>
                  <MenuList fontSize="sm" minW="220px" zIndex={30}>
                    <MenuItem
                      fontWeight={mappedSortOption === "mapping" ? "700" : "400"}
                      icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                      onClick={() => setMappedSortOption("mapping")}
                    >
                      Default order
                    </MenuItem>
                    <MenuItem
                      fontWeight={mappedSortOption === "name-asc" ? "700" : "400"}
                      icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                      onClick={() => setMappedSortOption("name-asc")}
                    >
                      Name A→Z
                    </MenuItem>
                    <MenuItem
                      fontWeight={mappedSortOption === "name-desc" ? "700" : "400"}
                      icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                      onClick={() => setMappedSortOption("name-desc")}
                    >
                      Name Z→A
                    </MenuItem>
                    <MenuItem
                      fontWeight={mappedSortOption === "created" ? "700" : "400"}
                      icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                      onClick={() => setMappedSortOption("created")}
                    >
                      Created (newest)
                    </MenuItem>
                    <MenuItem
                      fontWeight={mappedSortOption === "modified" ? "700" : "400"}
                      icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                      onClick={() => setMappedSortOption("modified")}
                    >
                      Modified (newest)
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
              <Box
                flex={1}
                minH={0}
                overflowY="auto"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                sx={MAP_PANEL_SCROLL_SX}
              >
                {rightPanelRowCount === 0 ? (
                  <Box py={8} px={3} textAlign="center">
                    <Text color="gray.500" fontSize="sm" mb={0}>
                      No mapped columns yet. Click a row on the left to assign ontology.
                    </Text>
                  </Box>
                ) : mappedVisibleIds.length === 0 ? (
                  <Box py={8} px={3} textAlign="center">
                    <Text color="gray.500" fontSize="sm" mb={0}>
                      No columns match your search.
                    </Text>
                  </Box>
                ) : (
                  mappedVisibleIds.map((id) => {
                    const index = columnIndexById(columns, id);
                    if (index < 0) return null;
                    const column = columns[index];
                    const mapped = isLogicalMapped(column);
                    const pending = pendingPromoteIds.has(id);
                    const isNew = Boolean(id && addedColumnIds.has(id));
                    const isUpdated = Boolean(id && updatedColumnIds.has(id));
                    const needsOntologyAssignment = pending && !mapped;
                    return (
                      <Box
                        key={`right-${id}`}
                        px={2}
                        py={2}
                        bg="white"
                        borderRadius="md"
                        mx={needsOntologyAssignment ? 0 : 1}
                        my={needsOntologyAssignment ? 0 : 0.5}
                        {...(needsOntologyAssignment
                          ? { borderWidth: "2px", borderColor: "orange.300" }
                          : { borderBottomWidth: "1px", borderBottomColor: "gray.100" })}
                      >
                        <Grid
                          templateColumns={MAPPED_ROW_GRID}
                          gap={2}
                          alignItems="center"
                          w="100%"
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minH="28px"
                            overflow="visible"
                          >
                            {mappedRowStatusDot({ mapped, isNew, pending: needsOntologyAssignment })}
                          </Box>
                          <HStack minW={0} spacing={2} align="center" flexWrap="wrap">
                            <Text fontWeight="700" color="gray.900" mb={0} fontSize="sm" noOfLines={2}>
                              {column.header?.name || ""}
                            </Text>
                            {isNew && mapped && (
                              <Badge colorScheme="blue" fontSize="9px" variant="subtle">
                                new
                              </Badge>
                            )}
                            {isUpdated && (
                              <Badge colorScheme="orange" fontSize="9px" variant="subtle">
                                updated
                              </Badge>
                            )}
                            <Badge fontSize="10px" colorScheme="gray" variant="subtle" flexShrink={0}>
                              {column.data_type !== null ? dataTypeToJSON(column?.data_type!) : "—"}
                            </Badge>
                          </HStack>
                          <Text color="gray.400" fontWeight="700" mb={0} textAlign="center" fontSize="sm" aria-hidden>
                            →
                          </Text>
                          <Box minW={0}>
                            <OntologyEntitySelector
                              logicalColumnList={columns}
                              kgColumnList={kgColumns}
                              metricList={getCombinedMetrics(column.header?.id!)}
                              kgSelection={dependentKgColumns.get(column.header?.id || "")?.[0] ?? null}
                              columnIndex={index}
                              logicalColumn={column}
                              dependentKgColumns={dependentKgColumns}
                              onKgSelectionChange={(h) => onKgSelectionChange(index, h)}
                              onOpenCreateKGColumnModal={handleOpenCreateKGColumnModal}
                              onOpenCreateMetricModal={handleOpenCreateMetricModal}
                              autoOpenMenu={autoOpenColumnId === id}
                              onAfterMenuClose={(hasSel) => handleRightSelectorMenuClose(id, hasSel)}
                            />
                          </Box>
                        </Grid>
                      </Box>
                    );
                  })
                )}

                {visibleRemovedColumns.length > 0 && (
                  <Box borderTopWidth="2px" borderColor="gray.200" mt={2} pt={2}>
                    <Button
                      variant="ghost"
                      size="sm"
                      w="100%"
                      justifyContent="flex-start"
                      fontWeight="700"
                      color="gray.700"
                      onClick={() => setRemovedSectionOpen((o) => !o)}
                      rightIcon={
                        <Text as="span" mb={0} fontSize="xs">
                          {removedSectionOpen ? "▾" : "▸"}
                        </Text>
                      }
                    >
                      Removed ({visibleRemovedColumns.length})
                    </Button>
                    <Collapse in={removedSectionOpen} animateOpacity>
                      <VStack align="stretch" spacing={0} mt={1}>
                        {visibleRemovedColumns.map((column, rIndex) => (
                          <HStack
                            key={`removed-${column.header?.id ?? rIndex}`}
                            px={2}
                            py={2}
                            bg="red.50"
                            borderBottomWidth="1px"
                            borderColor="gray.100"
                            justify="space-between"
                            align="center"
                          >
                            <HStack minW={0} spacing={2} flex={1}>
                              <Text
                                fontWeight="600"
                                color="red.700"
                                mb={0}
                                fontSize="sm"
                                textDecoration="line-through"
                                noOfLines={1}
                              >
                                {column.header?.name || ""}
                              </Text>
                              <Badge colorScheme="red" fontSize="9px" variant="subtle" flexShrink={0}>
                                removed
                              </Badge>
                            </HStack>
                            <IconButton
                              aria-label="Dismiss removed column"
                              icon={<CloseIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => {
                                const rid = column.header?.id;
                                if (rid) setDismissedRemovedIds((s) => new Set(s).add(rid));
                              }}
                            />
                          </HStack>
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )}
              </Box>
            </Flex>
          </Flex>
        </Flex>
      )}

      <Modal isOpen={isKGModalOpen} onClose={onKGModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display="flex" alignItems="center" gap={3} pr={10}>
            <Icon as={iconForKgColumn(kgModalColumnType)} boxSize={6} color="purple.600" flexShrink={0} aria-hidden />
            <Text as="span" fontSize="lg" fontWeight="bold" mb={0}>
              {kgCreateModalTitle(kgModalColumnType)}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isKGModalOpen && (
              <CreateKGColumnComponent
                key={`${kgModalColumnIndex}-${kgModalColumnType}-${kgModalDefaultName}`}
                defaultName={kgModalDefaultName}
                colType={kgModalColumnType}
                refreshKGColumns={refreshKGColumns}
                onClose={onKGModalClose}
                onSuccess={handleKGColumnCreated}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Metric creation modal — pre-selects the triggering logical column as the observation column.
          updateFlag=true  → creates immediately via API (onSuccess auto-maps).
          updateFlag=false → defers to metricRequestList; created after model save (onDefer queues). */}
      <Modal isOpen={isMetricModalOpen} onClose={onMetricModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Metric</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" maxH="80vh">
            {isMetricModalOpen && (
              <EditMetricDefinitionContainer
                createMetricFlag={true}
                modelInfo={{ ...(modelInfo ?? {}), columns } as ModelInfo}
                metricHeader={null}
                onClose={onMetricModalClose}
                onSuccess={updateFlag ? handleMetricCreated : undefined}
                onDefer={!updateFlag ? handleMetricDeferred : undefined}
                defaultObservationColumn={
                  metricModalLogicalColumn?.header?.id
                    ? ObservationColumnProto.create({
                      logical_column_header: { id: metricModalLogicalColumn.header.id },
                      aggregation: AggregationType.SUM,
                    })
                    : undefined
                }
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default OntologyMapping;
